import { apiGet, API_BASE, getAuthHeaders } from '@/utils/api';
import type {
  AdminAttendanceFilters,
  StudentAttendanceRow,
  StudentAttendanceDetail,
  ClassAttendanceRow,
  ModuleAttendanceRow,
  AbsenceAlertRow,
  AttendanceAnalyticsSummary,
} from '@/types/admin-attendance.types';

function buildParams(filters: AdminAttendanceFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.campusIds?.length) params.append('campusIds', filters.campusIds.join(','));
  if (filters.academicYearId) params.append('academicYearId', filters.academicYearId);
  if (filters.semesterNumbers?.length) params.append('semesterNumbers', filters.semesterNumbers.join(','));
  if (filters.filiereIds?.length) params.append('filiereIds', filters.filiereIds.join(','));
  if (filters.niveaux?.length) params.append('niveaux', filters.niveaux.join(','));
  if (filters.search) params.append('search', filters.search);
  return params;
}

export async function getAttendanceAnalytics(filters: AdminAttendanceFilters): Promise<AttendanceAnalyticsSummary> {
  const params = buildParams(filters);
  return apiGet<AttendanceAnalyticsSummary>(`/admin-attendance/analytics?${params.toString()}`);
}

export async function getStudentAttendance(filters: AdminAttendanceFilters): Promise<StudentAttendanceRow[]> {
  const params = buildParams(filters);
  return apiGet<StudentAttendanceRow[]>(`/admin-attendance/by-student?${params.toString()}`);
}

export async function getStudentAttendanceDetail(studentId: string, filters: AdminAttendanceFilters): Promise<StudentAttendanceDetail[]> {
  const params = buildParams(filters);
  return apiGet<StudentAttendanceDetail[]>(`/admin-attendance/student-detail/${studentId}?${params.toString()}`);
}

export async function getClassAttendance(filters: AdminAttendanceFilters): Promise<ClassAttendanceRow[]> {
  const params = buildParams(filters);
  return apiGet<ClassAttendanceRow[]>(`/admin-attendance/by-class?${params.toString()}`);
}

export async function getModuleAttendance(filters: AdminAttendanceFilters): Promise<ModuleAttendanceRow[]> {
  const params = buildParams(filters);
  return apiGet<ModuleAttendanceRow[]>(`/admin-attendance/by-module?${params.toString()}`);
}

export async function getAbsenceAlerts(filters: AdminAttendanceFilters): Promise<AbsenceAlertRow[]> {
  const params = buildParams(filters);
  return apiGet<AbsenceAlertRow[]>(`/admin-attendance/alerts?${params.toString()}`);
}

// ===== Export Functions =====

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

function getDateSuffix(): string {
  const now = new Date();
  return `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}${String(now.getDate()).padStart(2, '0')}`;
}

function getExportHeaders(): Record<string, string> {
  // Use getAuthHeaders but remove Content-Type for blob downloads
  const headers = getAuthHeaders();
  delete headers['Content-Type'];
  return headers;
}

export async function exportAttendanceExcel(filters: AdminAttendanceFilters): Promise<void> {
  const params = buildParams(filters);
  const response = await fetch(`${API_BASE}/admin-attendance/export/excel?${params.toString()}`, {
    method: 'GET',
    headers: getExportHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export Excel");
  }
  const blob = await response.blob();
  downloadBlob(blob, `rapport-absences-${getDateSuffix()}.xlsx`);
}

export async function exportAttendancePdf(filters: AdminAttendanceFilters): Promise<void> {
  const params = buildParams(filters);
  const response = await fetch(`${API_BASE}/admin-attendance/export/pdf?${params.toString()}`, {
    method: 'GET',
    headers: getExportHeaders(),
  });
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export PDF");
  }
  const blob = await response.blob();
  downloadBlob(blob, `rapport-absences-${getDateSuffix()}.pdf`);
}

export async function exportStudentReport(
  studentId: string,
  filters: AdminAttendanceFilters,
  scope: 'current' | 'all',
): Promise<void> {
  const params = buildParams(filters);
  params.append('scope', scope);
  const response = await fetch(
    `${API_BASE}/admin-attendance/export/student-report/${studentId}?${params.toString()}`,
    {
      method: 'GET',
      headers: getExportHeaders(),
    },
  );
  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || "Erreur lors de l'export du rapport");
  }
  const blob = await response.blob();
  downloadBlob(blob, `rapport-assiduite-${getDateSuffix()}.pdf`);
}
